import Color from 'color';

/**
 * Library color system - single source of truth for library colors
 */

// Known libraries with predetermined colors
const LIBRARY_COLOR_MAP: Record<string, string> = {
	// shadcn: '#3B82F6', // Blue
	// aceternity: '#9333EA', // Purple
	// 'magic-ui': '#10B981', // Emerald
	// 'cult-ui': '#F59E0B', // Amber
	// 'motion-primitives': '#06B6D4', // Cyan
};

/**
 * Generates a consistent, aesthetically pleasing color based on string input
 * @param input The string to generate a color from
 * @returns HSL color string
 */
export function generateLibraryColor(input: string): string {
	// Create a more robust hash of the string
	let hash = 0;
	for (let i = 0; i < input.length; i++) {
		// Improved hash function for better distribution
		hash = ((hash << 5) - hash) + input.charCodeAt(i);
		hash = hash & hash; // Convert to 32bit integer
	}

	// Create deterministic but well-distributed hue
	// Using prime numbers and the golden ratio for optimal distribution
	const prime = 31;
	const goldenRatio = 0.618033988749895;

	// Get a positive hash value
	const positiveHash = Math.abs(hash);

	// Calculate the hue using golden ratio for better distribution
	// Multiply by prime to avoid patterns
	const hue = ((positiveHash * prime * goldenRatio) % 1) * 360;

	// Create a palette of pleasing combinations with distinct saturation and lightness values
	const combinations = [
		{ s: 85, l: 60 }, // Vibrant but not overwhelming
		{ s: 75, l: 65 }, // Clear and bright
		{ s: 65, l: 55 }, // Balanced middle tone
		{ s: 80, l: 45 }, // Deep and rich
		{ s: 90, l: 70 }, // Bright and high-energy
		{ s: 70, l: 50 }, // Solid midtone
		{ s: 60, l: 60 }, // Softer medium tone
		{ s: 85, l: 75 }, // Light but colorful
	];

	// Select a combination based on the string, ensuring different strings get different combinations
	const index = positiveHash % combinations.length;
	const { s, l } = combinations[index];

	return Color.hsl(hue, s, l).toString();
}

/**
 * Returns the base color for a library
 * @param library The library identifier
 * @returns The color as a string in the format specified
 */
export function getLibraryColor(library: string): string {
	const normalizedLib = library.toLowerCase();

	return normalizedLib in LIBRARY_COLOR_MAP
		? LIBRARY_COLOR_MAP[normalizedLib]
		: generateLibraryColor(library);
}

/**
 * Color application helpers
 */

interface ColorOptions {
	opacity?: number; // 0-100
	alpha?: number; // 0-1
	lighten?: number; // 0-100
	darken?: number; // 0-100
}

/**
 * Get library color as a CSS color value with optional modifications
 */
export function getLibraryColorValue(library: string, options: ColorOptions = {}): string {
	let color = Color(getLibraryColor(library));

	if (options.opacity !== undefined) {
		color = color.alpha(options.opacity / 100);
	} else if (options.alpha !== undefined) {
		color = color.alpha(options.alpha);
	}

	if (options.lighten) {
		color = color.lighten(options.lighten / 100);
	} else if (options.darken) {
		color = color.darken(options.darken / 100);
	}

	return color.toString();
}

/**
 * Get library background color as a CSS value
 */
export function getLibraryBgColor(library: string, options: ColorOptions = {}): string {
	return getLibraryColorValue(library, options);
}

/**
 * Get library text color as a CSS value
 */
export function getLibraryTextColor(library: string, options: ColorOptions = {}): string {
	// By default, text colors are slightly darker than their base colors
	const darkOptions = { ...options, darken: (options.darken || 0) + 20 };
	return getLibraryColorValue(library, darkOptions);
}

/**
 * Get library colors for component styling (returns object with color values)
 */
export function getLibraryColors(library: string): {
	borderColor: string;
	backgroundColor: string;
	textColor: string;
} {
	const baseColor = getLibraryColor(library);
	const colorObj = Color(baseColor);

	return {
		borderColor: baseColor,
		backgroundColor: colorObj.alpha(0.1).toString(),
		textColor: colorObj.darken(0.2).toString(),
	};
}

/**
 * Get CSS style object for library styling
 * For use with inline style property
 */
export function getLibraryStyles(library: string): React.CSSProperties {
	const colors = getLibraryColors(library);

	return {
		borderColor: colors.borderColor,
		backgroundColor: colors.backgroundColor,
		color: colors.textColor,
	};
}

/**
 * Format a library ID for display
 */
export function formatLibraryName(id: string): string {
	if (id === "all") return "All Libraries";

	// Convert kebab-case or snake_case to proper case
	return id
		.replace(/[-_]+/g, ' ')
		.replace(/\w\S*/g, (text) => {
			return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
		});
}
